import React, { forwardRef, memo, useMemo } from 'react';
import { Crumbs, List, ListElement, ListLink, StyledDivider } from './styled';
/**
 * Represents a breadcrumb navigation component that displays a path as a series of links.
 * @param {Map<string, string> | BreadcrumbItem[] | Array<[string, string]>} props.path - The path to be displayed as breadcrumbs:
 *   - Map<string, string>: Where each entry is a label and its corresponding URL.
 *   - BreadcrumbItem[]: Array of objects with label and optional url.
 *   - Array<[string, string]>: Array of tuples with [label, url].
 * @param {string} [props.className] - Additional CSS class names to be applied to the component.
 * @param {Function} [props.itemRenderer] - Custom renderer for breadcrumb items.
 * @param {ReactNode} [props.divider] - Custom divider between breadcrumb items.
 * @param {ComponentType} [props.linkComponent] - Custom component for rendering links.
 * @param {ComponentType} [props.listComponent] - Custom component for the breadcrumbs list.
 * @param {ComponentType} [props.itemComponent] - Custom component for individual breadcrumb items.
 */
const Breadcrumbs = memo(forwardRef(function Breadcrumbs({ path, itemRenderer, divider, linkComponent, listComponent, itemComponent, ...rest }, ref) {
    // Custom components
    const LinkComp = linkComponent || ListLink;
    const ListComp = listComponent || List;
    const ItemComp = itemComponent || ListElement;
    const breadcrumbItems = useMemo(() => {
        if (path instanceof Map) {
            return Array.from(path).map(([label, url]) => ({ label, url: url || undefined }));
        }
        else if (Array.isArray(path) && path.length > 0) {
            if (Array.isArray(path[0])) {
                return path.map(([label, url]) => ({
                    label,
                    url: url || undefined
                }));
            }
            else {
                return path;
            }
        }
        return [];
    }, [path]);
    const breadcrumbsList = useMemo(() => {
        return breadcrumbItems.map((item, index, arr) => {
            const isLast = index === arr.length - 1;
            // Default rendering
            let content;
            if (itemRenderer) {
                // Use custom renderer if provided
                content = itemRenderer(item, isLast, index);
            }
            else {
                // Default rendering logic
                content = (React.createElement(React.Fragment, null,
                    item.url ? (React.createElement(LinkComp, { to: item.url }, item.label)) : (React.createElement("span", null, item.label)),
                    !isLast && (divider || React.createElement(StyledDivider, null))));
            }
            const itemKey = `breadcrumb-${item.label}-${index}`;
            return (React.createElement(ItemComp, { key: itemKey, className: isLast ? 'last-item' : '' }, content));
        });
    }, [breadcrumbItems, itemRenderer, divider, LinkComp, ItemComp]);
    return (React.createElement(Crumbs, { ref: ref, ...rest },
        React.createElement(ListComp, null, breadcrumbsList)));
}));
export default Breadcrumbs;
